import {
  Component,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  Output,
  EventEmitter,
  Input,
  OnChanges,
  SimpleChanges,
  OnDestroy,
} from '@angular/core';
import { AgGridAngular } from '@ag-grid-community/angular';
import { AllModules } from '@ag-grid-enterprise/all-modules';

import {
  agGridLocale,
  PercentageCellRenderer,
  NoDecimalCellRenderer,
} from 'app/utils/ag-grid.locale';
import * as moment from 'moment';
import { Observable, Subject } from 'rxjs';
import { CompaniesUISelectors } from 'app/main/company/selectors';
import { select, Store } from '@ngrx/store';
import { AppState } from 'app/reducers';
import { IPlEntry, IPlColumnEntry } from '../../pl.types';
import { PlSelectors, PlColumnSelectors } from '../../selectors';
import { PlActions, PlColumnActions } from '../../actions';
import { takeUntil, map, combineAll } from 'rxjs/operators';
import { CompanySubset } from 'app/api/company.api.service';

@Component({
  selector: 'app-pl-aggrid',
  templateUrl: './pl-aggrid.component.html',
  styleUrls: ['./pl-aggrid.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PlAggridComponent implements OnInit, OnChanges, OnDestroy {
  public gridApi;
  public gridColumnApi;

  public columnDefs;
  public defaultColDef = {
    sortable: true,
  };
  public sideBar;
  public statusBar;
  public rowBuffer;
  public localeText;
  public modules;
  private unsubscribeAll = new Subject();

  @Input() suppressRowDrag;
  @Input() showAna3: boolean;

  ana3IndexColumn = 1;

  fieldType: string;
  years: string[] = [];
  rowData$: Observable<IPlEntry[]>;
  selectedRows: IPlEntry[];

  rowDataWithAna3: IPlEntry[];
  rowDataWithoutAna3: IPlEntry[];

  plColumns: IPlColumnEntry[];

  DATA: {
    elements: {
      [ana2Group: string]: {
        [timeline: string]: number;
      };
    };
    groups: {
      [ana2Group: string]: string[];
    };
    ana2: {
      [timeline: string]: {
        [ana2Name: string]: number;
      };
    };
    ana3: {
      [timeline: string]: {
        [ana2Name: string]: {
          [ana3Name: string]: number;
        };
      };
    };
    ana3ListByAna2: {
      [ana2Name: string]: string[];
    };
    rowSpanned: {
      [ana2Name: string]: boolean;
    };
  };

  @Output() selectionChanged = new EventEmitter();
  @Output() plAgGridReady = new EventEmitter();

  @ViewChild('agGrid', { static: false }) agGrid: AgGridAngular;

  constructor(private store: Store<AppState>) {
    this.modules = AllModules;
    this.localeText = agGridLocale.fr;
    this.statusBar = {
      statusPanels: [
        {
          statusPanel: 'agTotalRowCountComponent',
          align: 'left',
        },
        {
          statusPanel: 'agAggregationComponent',
        },
        { statusPanel: 'agSelectedRowCountComponent' },
        { statusPanel: 'agFilteredRowCountComponent' },
      ],
    };
  }

  ngOnChanges(simpleChange: SimpleChanges): void {
    if (simpleChange.suppressRowDrag) {
      if (this.agGrid) {
        this.agGrid.api.setSuppressRowDrag(this.suppressRowDrag);
        this.columnDefs[0].dndSource = !this.suppressRowDrag;
        this.agGrid.api.setColumnDefs(this.columnDefs);
        this.autoSizeAll(this.agGrid);
      }
    }

    if (
      simpleChange.showAna3 &&
      typeof simpleChange.showAna3.currentValue === 'boolean'
    ) {
      if (this.DATA) {
        this.DATA.rowSpanned = {};
      }
      if (this.agGrid) {
        if (simpleChange.showAna3.currentValue) {
          this.agGrid.api.setRowData(this.rowDataWithAna3);
        } else {
          this.agGrid.api.setRowData(this.rowDataWithoutAna3);
        }
      }
    }
  }

  ngOnInit(): void {
    this.columnDefs = this.getColumnDefs();

    this.rowData$ = this.store.pipe(
      takeUntil(this.unsubscribeAll),
      select(PlSelectors.selectPl),
      map((rows) => {
        if (this.DATA) {
          this.DATA.rowSpanned = {};
        }
        const rowData = [];
        rows.forEach((data) => {
          if (data.axis && data.axis === 'ana2') {
            const ana2Name = data.name;
            if (this.DATA) {
              const ana3List = this.DATA.ana3ListByAna2[ana2Name] || [];
              ana3List.forEach((ana3Name) => {
                const d = { ...data };
                d.ana3Name = ana3Name;
                rowData.push(d);
              });
              rowData.push({
                ...data,
                ana3Name: 'Total',
                cssClass: ['cssTotal'],
              });
            }
          } else {
            rowData.push(data);
          }
        });
        this.rowDataWithAna3 = rowData;
        this.rowDataWithoutAna3 = rows;
        return this.showAna3 ? this.rowDataWithAna3 : this.rowDataWithoutAna3;
      })
    );

    this.store
      .pipe(
        takeUntil(this.unsubscribeAll),
        select(PlColumnSelectors.selectPlColumns)
      )
      .subscribe((columnDefs) => {
        this.plColumns = columnDefs;
        let columnCountWithoutCustomColumn = 3;

        if (this.years.length === 2) {
          columnCountWithoutCustomColumn = 4;
        }

        const plColumnDefs = this.columnDefs.slice(
          0,
          columnCountWithoutCustomColumn
        );

        if (this.plColumns.length > columnCountWithoutCustomColumn) {
          for (
            let index = columnCountWithoutCustomColumn;
            this.plColumns.length > index;
            index++
          ) {
            plColumnDefs.push({
              headerName: this.plColumns[index].name,
              width: 125,
              cellClass: this.dynamicCellClass,
              cellRenderer: this.dynamicRenderer,
              valueGetter: ({ data }) => {
                const columns = this.plColumns[index].columns || [];
                let element = data.ana3Name || data.name;
                if (element === 'Total') {
                  element = data.name;
                }
                const value = columns
                  .map((col) => {
                    return (
                      (this.DATA.elements[element] &&
                        this.DATA.elements[element][col.name]) ||
                      0
                    );
                  })
                  .reduce((a, b) => a + b, 0);

                if (this.DATA.elements[element]) {
                  this.DATA.elements[element][this.plColumns[index].name] =
                    value || 0;
                }
                data.value = value || null;
                return data.value;
              },
            });
          }

          if (this.agGrid) {
            this.columnDefs = plColumnDefs;
            this.agGrid.api.setColumnDefs(plColumnDefs);
            setTimeout(() => {
              this.autoSizeAll(this.agGrid);
            });
          }
        }
      });

    this.store
      .pipe(takeUntil(this.unsubscribeAll), select(PlSelectors.selectAnaPL))
      .subscribe((payload: any) => {
        this.DATA = payload;
      });

    this.store
      .pipe(
        takeUntil(this.unsubscribeAll),
        select(CompaniesUISelectors.selectCurrentPeriod)
      )
      .subscribe((periods) => {
        const selectedYears = [];
        if (periods) {
          if (periods.from) {
            selectedYears.push(moment(periods.from.startDate).format('YYYY'));
          }
          if (periods.to) {
            selectedYears.push(moment(periods.to.startDate).format('YYYY'));
          }

          this.years = selectedYears;
        } else {
          this.years = [];
        }
        if (this.agGrid) {
          setTimeout(() => {
            this.onGridReady(this.agGrid);
          }, 1000); // set timeout to skip freezen on select dropdown date
        }
      });
  }

  ngOnDestroy(): void {
    this.unsubscribeAll.next();
    this.unsubscribeAll.complete();
  }

  onRowDragEnd(ev): void {
    const input: IPlEntry[] = [];
    this.gridApi.forEachNode((node) => {
      if (node.data) {
        const data = node.data as IPlEntry;
        data.sortOrder = node.rowIndex + 1;
        input.push(data);
      }
    });

    this.store.dispatch(PlActions.plLoaded({ entries: input }));
  }

  getColumnDefs(): any[] {
    return [
      {
        headerName: '',
        field: 'name',
        colId: 'nameColId',
        pinned: 'left',
        rowDrag: true,
        dndSource: false,
        rowSpan: ({ data }) => {
          if (data && data.axis === 'ana2' && data.ana3Name) {
            const ana2Name = data.name;
            if (this.DATA) {
              if (!this.DATA.hasOwnProperty('rowSpanned')) {
                this.DATA.rowSpanned = {};
              }
              if (this.DATA.rowSpanned.hasOwnProperty(ana2Name)) {
                return 0;
              }

              this.DATA.rowSpanned[ana2Name] = true;
              let ana3RowCount = 0;
              if (this.DATA.ana3ListByAna2[data.name]) {
                ana3RowCount = this.DATA.ana3ListByAna2[data.name].length || 0;
              }
              return ana3RowCount + 1;
            }
          }
          return 0;
        },
        cellClass: ({ data }) => {
          if (data) {
            if (data.color) {
              return data.color;
            } else if (data && data.axis === 'ana2' && data.ana3Name) {
              return 'row-spanned';
            }
          }
        },
        valueGetter: ({ data }) => {
          if (data.fieldType === 'blank') {
            return null;
          }
          return data.name;
        },
      },
      {
        headerName: 'ANA 3',
        field: 'ana3Name',
        colId: 'Ana3ColId',
        pinned: 'left',
        hide: true,
        cellClass: ({ data }) => {
          if (data) {
            if (data.color) {
              return data.color;
            }
            if (data.ana3Name && data.ana3Name === 'Total') {
              return 'cssTotal';
            }
          }
          return '';
        },
        valueGetter: (params) => {
          return params.data.ana3Name || null;
        },
      },
    ];
  }

  dynamicRenderer(params): string {
    if (params.data && params.data.fieldType === 'calculated') {
      if (params.data.fieldOperator === 'percentage') {
        return PercentageCellRenderer(params);
      }
    }
    return NoDecimalCellRenderer(params);
  }

  dynamicCellClass({ data }): string[] {
    let css = ['ag-numeric-cell'];
    if (data && data.value < 0) {
      css.push('negative-flux');
    }
    if (data.fieldType === 'calculated') {
      css.push('field-calculated-css');
    }
    if (data.cssClass) {
      css = [...css, ...data.cssClass];
    }
    return css;
  }

  onGridReady(params): void {
    this.columnDefs = this.getColumnDefs();
    // Column start after timeline
    let colCountPosition = 1;

    // Add timeline
    this.years.forEach((selectedYear) => {
      colCountPosition++;
      // Add flux to column
      this.columnDefs.push({
        headerName: selectedYear,
        children: [],
      });

      this.columnDefs[colCountPosition].children.push({
        headerName: 'Total',
        field: selectedYear,
        // tslint:disable-next-line: no-shadowed-variable
        cellRenderer: this.dynamicRenderer,
        cellClass: this.dynamicCellClass,
        valueGetter: ({ data }) => {
          let element = data.ana3Name || data.name;
          if (element === 'Total') {
            element = data.name;
          }
          if (this.DATA.elements[element]) {
            data.value = 0;
            data.value = Object.values(this.DATA.elements[element]).reduce(
              (a, b) => a + b,
              0
            );

            if (data.fieldOperator === 'percentage') {
              return data.value / 12 || null;
            }
          }
          return data.value || null;
        },
        width: 100,
        columnGroupShow: 'open',
      });
      new Array(12).fill(0).forEach((d, index) => {
        const timelineKey = `${selectedYear}-${
          index < 9 ? '0' + (index + 1) : index + 1
        }`;
        this.columnDefs[colCountPosition].children.push({
          headerName: timelineKey,
          width: 100,
          columnGroupShow: 'closed',
          // tslint:disable-next-line: no-shadowed-variable
          cellRenderer: this.dynamicRenderer,
          cellClass: this.dynamicCellClass,
          valueGetter: ({ data }) => {
            if (data.fieldType === 'blank') {
              return null;
            }

            if (data.fieldType === 'calculated') {
              if (data.fieldOperator === 'sum') {
                const list = data.rows.map((r) => r.name);
                data.value =
                  list
                    .map((groupName) => {
                      return this.DATA.elements[groupName][timelineKey];
                    })
                    .reduce((a, b) => a + b, 0) || 0;
              } else if (data.fieldOperator === 'percentage') {
                const numerators = data.rows
                  .filter((r) => r.categoryCalculated === 'numerator')
                  .map((r) => r.name);
                const denominators = data.rows
                  .filter((r) => r.categoryCalculated === 'denominator')
                  .map((r) => r.name);

                data.value =
                  (this.DATA.elements[numerators[0]][timelineKey] /
                    this.DATA.elements[denominators[0]][timelineKey]) *
                    100 || 0;
              }

              if (!this.DATA.elements.hasOwnProperty(data.name)) {
                this.DATA.elements[data.name] = {};
              }

              this.DATA.elements[data.name][timelineKey] = data.value || 0;

              data.value = data.value || 0;

              return data.value;
            }

            if (this.DATA) {
              if (data.fieldType === 'ana2') {
                if (this.DATA.ana2[timelineKey]) {
                  if (data.ana3Name) {
                    if (data.ana3Name === 'Total') {
                      data.value = this.DATA.ana2[timelineKey][data.name] || 0;
                    } else {
                      if (
                        this.DATA.ana3[timelineKey] &&
                        this.DATA.ana3[timelineKey][data.name]
                      ) {
                        data.value =
                          this.DATA.ana3[timelineKey][data.name][
                            data.ana3Name
                          ] || 0;
                      }
                    }
                  } else {
                    data.value = this.DATA.ana2[timelineKey][data.name] || 0;
                  }
                }
              } else {
                const ana2List = this.DATA.groups[data.name];
                data.value =
                  ana2List
                    .map((ana2Name) => {
                      if (this.DATA.ana2[timelineKey]) {
                        if (
                          this.DATA.ana2[timelineKey].hasOwnProperty(ana2Name)
                        ) {
                          return this.DATA.ana2[timelineKey][ana2Name];
                        }
                        return 0;
                      }
                    })
                    .reduce((a, b) => a + b, 0) || 0;
              }

              if (!this.DATA.hasOwnProperty('elements')) {
                this.DATA.elements = {};
              }

              const element = data.ana3Name || data.name;

              if (!this.DATA.elements.hasOwnProperty(element)) {
                this.DATA.elements[element] = {};
              }

              this.DATA.elements[element][timelineKey] = data.value || 0;
              data.value = data.value || 0;
              return data.value;
            }

            return 0;
          },
        });
      });
    });

    this.store.dispatch(
      PlColumnActions.plColumnLoadRequested({
        entries: this.columnDefs.map((col) => {
          return {
            name: col.headerName,
            key: col.key || new Date().valueOf() + '' + col.headerName,
            children: col.children,
            type: col.colType || 'default',
          };
        }),
      })
    );
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    console.log('Grid Ready');
    this.plAgGridReady.emit(params);
  }

  private autoSizeAll(params): void {
    const allColumnIds = params.columnApi
      .getAllColumns()
      .map((column) => column.colId);
    setTimeout(() => params.columnApi.autoSizeColumns(allColumnIds), 0);
  }

  onSelectionChanged(params): void {
    this.selectedRows = params.api.getSelectedRows();
    this.selectionChanged.emit(this.selectedRows);
  }

  refreshColumnDef(): void {
    if (this.agGrid) {
      console.log('Show Ana 3', this.showAna3);
      this.agGrid.columnApi.setColumnVisible('Ana3ColId', this.showAna3);
    }
  }

  onRowDataChanged(params): void {
    this.selectedRows = params.api.getSelectedRows();
    this.selectionChanged.emit(this.selectedRows);
    this.refreshColumnDef();
    this.autoSizeAll(params);
  }
}
